import {
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import React, { useState } from "react";
import {
  validateDescriptionField,
  validateSnakeCaseName,
} from "../../../../../utils/helpers";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { CalculationTypesEndPointLogic } from "../../../../../logic/DataCollection/Unifier/CalculationTypesEndpointLogic";

interface CreateCalculationProps {
  open: boolean;
  handleClose: () => void;
}

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Paper sx={{ padding: "16px", mb: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const CreateCalculationType = ({
  open,
  handleClose,
}: CreateCalculationProps) => {
  const [loading, setLoading] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [atomPhrase, setAtomPhrase] = useState("");
  const [customExpression, setCustomExpression] = useState("");
  const [deepViewExplanation, setDeepViewExplanation] = useState("");
  const [plainEnglishText, setPlainEnglishText] = useState("");
  // a new type is offered to Deep View unless the author says otherwise
  const [isActive, setIsActive] = useState(true);
  const [isDocumented, setIsDocumented] = useState(false);
  const [hasMissedRunWarning, setHasMissedRunWarning] = useState(false);

  const [error, setError] = useState({
    name: "",
    description: "",
    atomPhrase: "",
  });

  //-- Function to close the drawer and reset the form
  const closeCreateCollectionDrawer = () => {
    setName("");
    setDescription("");
    setAtomPhrase("");
    setCustomExpression("");
    setDeepViewExplanation("");
    setPlainEnglishText("");
    setIsActive(true);
    setIsDocumented(false);
    setHasMissedRunWarning(false);
    setError({ name: "", description: "", atomPhrase: "" });
    handleClose();
  };

  //-- Function to check if the form is valid
  const isFormValid = () => {
    return (
      name?.trim() !== "" &&
      description?.trim() !== "" &&
      atomPhrase?.trim() !== "" &&
      !error.name &&
      !error.description &&
      !error.atomPhrase
    );
  };

  //-- Function to create new calculation type
  const handleCreateNewType = async () => {
    if (!isFormValid()) return;
    const data: any = {
      name,
      description,
      atom_phrase: atomPhrase,
      is_active: isActive ? 1 : 0,
      deep_view_is_documented: isDocumented ? 1 : 0,
      has_missed_run_warning: hasMissedRunWarning ? 1 : 0,
    };
    if (customExpression.trim()) {
      data.custom_expression = customExpression;
    }
    if (deepViewExplanation.trim()) {
      data.deep_view_explanation = deepViewExplanation;
    }
    if (plainEnglishText.trim()) {
      data.plain_english_text = plainEnglishText;
    }
    setLoading(true);
    try {
      const response =
        await CalculationTypesEndPointLogic.createNewCollectionTypes(
          oauthTokenHeader,
          data,
        );

      if (!response || !response.status) {
        throw new Error("Failed to create new calculation type.");
      } else if (response.status === 201) {
        await CalculationTypesEndPointLogic.getCollectionTypes(
          oauthTokenHeader,
          dispatch,
        );
        closeCreateCollectionDrawer();
        dispatch(
          setDisplayErrorSnackBar({
            message: "Calculation type has been created successfully.",
            type: "success",
          }),
        );
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "An unexpected error occurred.";

      dispatch(
        setDisplayErrorSnackBar({ message: errorMessage, type: "error" }),
      );

      return; // Stop execution, do not close the drawer
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      sx={{ zIndex: 1300 }}
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        closeCreateCollectionDrawer();
      }}
      PaperProps={{ sx: { width: "40%", minWidth: "480px" } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ padding: "16px" }}
        >
          <Typography variant="h3" sx={{ fontSize: "20px" }}>
            Create New Calculation Type
          </Typography>
          <IconButton
            data-tracker="377"
            onClick={closeCreateCollectionDrawer}
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ padding: "16px", overflowY: "auto", flex: 1 }}>
          <Section title="Details">
            <TextField
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setName(e.target.value);
                setError((prev) => ({
                  ...prev,
                  name: validateSnakeCaseName(e.target.value),
                }));
              }}
              error={!!error.name}
              helperText={error.name}
              required
              label="Calculation Name"
              placeholder="How to name this type"
              color="secondary"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDescription(e.target.value);
                setError((prev) => ({
                  ...prev,
                  description: validateDescriptionField(e.target.value),
                }));
              }}
              error={!!error.description}
              helperText={error.description}
              required
              label="Description"
              placeholder="What does this type do"
              color="secondary"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={atomPhrase}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setAtomPhrase(e.target.value);
                setError((prev) => ({
                  ...prev,
                  atomPhrase: e.target.value.trim()
                    ? ""
                    : "This field is required.",
                }));
              }}
              error={!!error.atomPhrase}
              helperText={error.atomPhrase}
              required
              label="Atom Phrase — Stage 2 Explanation"
              color="secondary"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              value={customExpression}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCustomExpression(e.target.value)
              }
              label="Custom Expression"
              color="secondary"
              size="small"
              fullWidth
            />
          </Section>

          <Section title="Deep View Documentation">
            <TextField
              value={deepViewExplanation}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDeepViewExplanation(e.target.value)
              }
              label="Deep View Explanation"
              color="secondary"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              value={plainEnglishText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPlainEnglishText(e.target.value)
              }
              label="Plain English Text"
              color="secondary"
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Section>

          <Section title="Settings">
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                }
                label="Set as active"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={isDocumented}
                    onChange={(event) => setIsDocumented(event.target.checked)}
                  />
                }
                label="Documented"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={hasMissedRunWarning}
                    onChange={(event) =>
                      setHasMissedRunWarning(event.target.checked)
                    }
                  />
                }
                label="Missed Run Warning"
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                data-tracker="376"
                variant="contained"
                color="secondary"
                loading={loading}
                loadingPosition="end"
                endIcon={loading && <SaveIcon />}
                disabled={!isFormValid()}
                onClick={handleCreateNewType}
              >
                Create new Type <AddIcon sx={{ width: "20px", ml: "5px" }} />
              </Button>
            </Stack>
          </Section>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CreateCalculationType;
